/**
 * Consent state — single source of truth para gating de tracking/analytics.
 *
 * NUNCA importes este archivo desde un componente con `'use client'` para usar
 * `getServerConsent()`. Usa `useConsent()` (hook cliente) o impórtalo desde un
 * RSC/Server Action para `getServerConsent()`.
 *
 * Política:
 *   - Default = todo denied (excepto `necessary` que siempre es true).
 *   - El banner CMP (FASE 13) llama `setConsent(...)` con la decisión del user.
 *   - Cookie `nqoro_consent` JSON, 1 año, sameSite=lax, secure en prod.
 *   - Reactividad: emite `window dispatchEvent('nqoro:consentchange')` al cambiar.
 *   - Server: lee la cookie via `next/headers`.
 *
 * Categorías (alineadas con TCF-style + CMP estándar):
 *   - necessary    → siempre true. Auth, carrito, CSRF, sesión.
 *   - preferences  → idioma, theme, configuración guardada.
 *   - analytics    → GA4, GTM, PostHog, Sentry replay (no Sentry crash reports).
 *   - marketing    → Meta Pixel/CAPI, TikTok Pixel/Events, retargeting.
 */

export const CONSENT_COOKIE_NAME = 'nqoro_consent';
export const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 año
export const CONSENT_VERSION = 1;

export type ConsentCategory = 'necessary' | 'preferences' | 'analytics' | 'marketing';

export interface ConsentState {
  version: number;
  necessary: true; // siempre true (no se puede desactivar)
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
  /** Timestamp ISO de cuando el usuario decidió (null si default sin decisión) */
  decidedAt: string | null;
}

export const DEFAULT_CONSENT: ConsentState = {
  version: CONSENT_VERSION,
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
  decidedAt: null,
};

/** Helper sync usado tanto en server como cliente para parsear cookie value. */
export function parseConsent(cookieValue: string | null | undefined): ConsentState {
  if (!cookieValue) return DEFAULT_CONSENT;
  try {
    const parsed = JSON.parse(cookieValue) as Partial<ConsentState>;
    if (parsed.version !== CONSENT_VERSION) return DEFAULT_CONSENT;
    return {
      version: CONSENT_VERSION,
      necessary: true,
      preferences: parsed.preferences === true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : null,
    };
  } catch {
    return DEFAULT_CONSENT;
  }
}

export function serializeConsent(state: ConsentState): string {
  return JSON.stringify(state);
}

/** ¿Categoría granted? Pequeño helper que centraliza el chequeo. */
export function hasConsent(state: ConsentState, category: ConsentCategory): boolean {
  if (category === 'necessary') return true;
  return state[category] === true;
}
